import { api } from '@/lib/api/client';
import { kstDateString } from './kstDate';
import type {
  LeadingSectorRef,
  LeadingStock,
  DailyPriceSlice,
  LadderGuide,
  AlertDirection,
  Candle,
  CandleInterval,
  ExchangeRateInfo,
  Price,
  MyTradeSummary,
  EntryIndicators,
  UsTaxEstimate,
  PriceAlert,
  SectorStock,
  SectorSummary,
  StockDetail,
  StockSummary,
  WatchlistCategory,
  WatchlistItem,
} from './stockTypes';

/**
 * 종목 검색 · 관심종목 · 차트 · 시세 API. (16단계)
 *
 * 🔴 토스를 직접 부르지 않는다
 *   분봉도 현재가도 전부 우리 백엔드를 거친다.
 *   프론트에서 토스를 부르면 토큰이 브라우저로 나간다. (CLAUDE.md 1.2절)
 */

/**
 * 종목 검색. 종목명 부분일치 또는 심볼 전방일치다.
 *
 * 두 글자 미만이면 서버도 빈 배열을 주지만, 호출부가 MIN_SEARCH_LENGTH 로
 * 먼저 걸러 왕복 자체를 없앤다.
 */
export async function searchStocks(
  query: string,
  country: 'KR' | 'US' = 'KR',
): Promise<StockSummary[]> {
  return api.get<StockSummary[]>('/trading/stocks', { q: query, country });
}

/**
 * 종목 상세.
 *
 * 🔴 유니버스 밖 종목은 서버가 지표를 그 자리에서 계산한다.
 *   응답이 수백 ms 느려지므로 호출부는 로딩 표시를 붙여야 한다.
 */
export async function fetchStockDetail(stockId: number): Promise<StockDetail> {
  return api.get<StockDetail>(`/trading/stocks/${stockId}`);
}

/**
 * 차트 봉 데이터. 오름차순으로 온다.
 *
 * 1d 는 DB 에서 읽어 빠르고, 1m 은 토스를 부르므로 눈에 띄게 느리다.
 */
export async function fetchCandles(
  stockId: number,
  interval: CandleInterval,
  count: number,
): Promise<Candle[]> {
  return api.get<Candle[]>(`/trading/stocks/${stockId}/candles`, { interval, count });
}

/**
 * 실시간 시세.
 *
 * 시세를 못 얻은 종목은 결과에서 빠진다. 거래정지·신규상장에서 생긴다.
 * 호출부가 stockId 로 대조해 없는 것을 "-" 로 표시한다.
 */
export async function fetchPrices(stockIds: number[]): Promise<Price[]> {
  if (stockIds.length === 0) {
    return [];
  }
  // axios 는 배열 파라미터를 stockIds[]=1&stockIds[]=2 로 보낸다.
  // 스프링의 List<Long> 바인딩은 콤마 구분 문자열을 그대로 받으므로 직접 잇는다.
  return api.get<Price[]>('/trading/prices', { stockIds: stockIds.join(',') });
}

export async function fetchWatchlist(): Promise<WatchlistItem[]> {
  return api.get<WatchlistItem[]>('/trading/watchlist');
}

/** 이미 담긴 종목이면 기존 항목이 그대로 온다(멱등). 상한을 넘으면 409 다. */
export async function addToWatchlist(
  stockId: number,
  memo?: string,
): Promise<WatchlistItem> {
  return api.post<WatchlistItem>('/trading/watchlist', { stockId, memo });
}

/** 없는 것을 지워도 200 이다. 낙관적 삭제 후 재시도가 안전하다. */
export async function removeFromWatchlist(stockId: number): Promise<boolean> {
  return api.delete<boolean>(`/trading/watchlist/${stockId}`);
}

export async function updateWatchlistMemo(
  stockId: number,
  memo: string,
): Promise<WatchlistItem> {
  return api.put<WatchlistItem>(`/trading/watchlist/${stockId}/memo`, { memo });
}

/**
 * 전체·미분류 탭의 순서를 통째로 보낸다. 응답은 갱신된 목록 전체다.
 *
 * 🔴 카테고리 안의 순서는 replaceWatchlistCategoryItems 다 (3.10)
 */
export async function reorderWatchlist(stockIds: number[]): Promise<WatchlistItem[]> {
  return api.put<WatchlistItem[]>('/trading/watchlist/order', { stockIds });
}

// ── 카테고리 (3.10) ──

export async function fetchWatchlistCategories(): Promise<WatchlistCategory[]> {
  return api.get<WatchlistCategory[]>('/trading/watchlist/categories');
}

/** 3개를 넘으면 409(T007), 같은 이름이 있으면 409(T009)다. */
export async function createWatchlistCategory(name: string): Promise<WatchlistCategory> {
  return api.post<WatchlistCategory>('/trading/watchlist/categories', { name });
}

export async function renameWatchlistCategory(
  categoryId: number,
  name: string,
): Promise<WatchlistCategory> {
  return api.put<WatchlistCategory>(`/trading/watchlist/categories/${categoryId}`, {
    name,
  });
}

/**
 * 카테고리를 지운다.
 *
 * 🔴 담긴 관심종목은 지워지지 않는다. 소속만 풀린다.
 *   응답이 그 건수다. 화면이 그대로 알린다.
 */
export async function deleteWatchlistCategory(categoryId: number): Promise<number> {
  return api.delete<number>(`/trading/watchlist/categories/${categoryId}`);
}

/**
 * 카테고리에 종목 하나를 담는다. 종목 상세 모달의 체크박스가 쓴다.
 *
 * 🔴 다른 카테고리에서 빼지 않는다. 한 종목이 여러 곳에 들어갈 수 있다.
 */
export async function addWatchlistCategoryItem(
  categoryId: number,
  stockId: number,
): Promise<void> {
  return api.post<void>(`/trading/watchlist/categories/${categoryId}/items`, { stockId });
}

/** 카테고리에서 종목 하나를 뺀다. 관심종목 자체는 남는다. */
export async function removeWatchlistCategoryItem(
  categoryId: number,
  stockId: number,
): Promise<boolean> {
  return api.delete<boolean>(
    `/trading/watchlist/categories/${categoryId}/items/${stockId}`,
  );
}

/**
 * 카테고리의 구성과 순서를 통째로 확정한다. 편집 모드의 저장이 쓴다.
 *
 * 목록에 없는 종목은 이 카테고리에서 빠진다. 빈 목록이면 카테고리를 비운다.
 * 순서 변경과 빼기를 한 요청으로 보내 하나만 성공한 중간 상태를 없앤다.
 */
export async function replaceWatchlistCategoryItems(
  categoryId: number,
  stockIds: number[],
): Promise<void> {
  return api.put<void>(`/trading/watchlist/categories/${categoryId}/items`, { stockIds });
}

// ── 저점매수 가격 알림 (2026-08-20) ──

/** 내 알림 전체. 발동해서 꺼진 것도 함께 온다. */
export async function fetchPriceAlerts(): Promise<PriceAlert[]> {
  return api.get<PriceAlert[]>('/trading/price-alerts');
}

/**
 * 알림을 하나 건다. 발동해 꺼진 알림의 재무장도 이것이다.
 *
 * 🔴 한 종목에 최대 5개 (2026-09-02, V123)
 *   방향과 기준단가가 둘 다 같으면 새로 만들지 않고 기존 것을 다시 켠다.
 *   5개를 넘으면 409(T011), 관심종목에 없는 종목이면 409(T010) 다.
 */
export async function setPriceAlert(
  stockId: number,
  targetPrice: number,
  direction: AlertDirection = 'BELOW',
): Promise<PriceAlert> {
  return api.put<PriceAlert>(`/trading/price-alerts/${stockId}`, { targetPrice, direction });
}

/**
 * 울려서 꺼진 알림을 다시 켠다. (2026-09-03 사용자 요청)
 *
 * 기준가는 그대로 두고 스위치만 올린다. 전에는 종목 모달을 열어
 * 같은 값으로 다시 저장해야 했다. 이미 켜져 있어도 성공한다(멱등).
 */
export async function rearmPriceAlert(alertId: number): Promise<PriceAlert> {
  return api.put<PriceAlert>(`/trading/price-alerts/entries/${alertId}/rearm`);
}

/**
 * 걸어둔 알림의 기준가를 고친다. (2026-09-28 사용자 요청, I-83 ②)
 *
 * 고치면 다시 켜진다. 방향은 못 바꾼다. 같은 자리의 알림이 이미 있으면 서버가 둘을
 * 하나로 합치고 그쪽을 돌려준다 - 돌아온 id 가 보낸 id 와 다를 수 있다.
 */
export async function changePriceAlertPrice(
  alertId: number,
  targetPrice: number,
): Promise<PriceAlert> {
  return api.put<PriceAlert>(`/trading/price-alerts/entries/${alertId}`, { targetPrice });
}

/**
 * 알림 한 건만 지운다. (2026-09-02, V123)
 *
 * 한 종목에 최대 5개가 걸리므로 "종목 + 방향" 으로는 하나를 특정할 수 없다.
 */
export async function removePriceAlertById(alertId: number): Promise<boolean> {
  return api.delete<boolean>(`/trading/price-alerts/entries/${alertId}`);
}

/**
 * 종목 하나의 알림을 지운다. 없는 것을 지워도 200 이다(멱등).
 *
 * 방향을 주면 그 방향의 알림만, 안 주면 그 종목의 알림을 전부 지운다.
 * 관심종목에서 뺄 때 후자를 쓴다.
 */
export async function removePriceAlert(
  stockId: number,
  direction?: AlertDirection,
): Promise<boolean> {
  // 방향을 주면 그것만, 안 주면 그 종목의 알림을 전부 지운다
  const query = direction === undefined ? '' : `?direction=${direction}`;
  return api.delete<boolean>(`/trading/price-alerts/${stockId}${query}`);
}

// ── 매매일지 (13단계, 2026-08-28) ──

/**
 * 내 실제 체결의 왕복 거래. 기간은 매도 시점 기준이다.
 *
 * 🔴 그 전에 산 것도 재료로 쓴다 - 6월에 사서 8월에 팔았으면 8월의 거래다.
 */
export async function fetchMyTrades(days: number): Promise<MyTradeSummary> {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  // 🔴 한국 날짜로 자른다 — UTC 로 자르면 09시 전엔 to 가 어제라 오늘 새벽 미국장 체결이 빠졌다 (9/28 I-78 2차)
  return api.get<MyTradeSummary>('/trading/account/my-trades', {
    from: kstDateString(from),
    to: kstDateString(to),
  });
}

/**
 * 매매일지 — 산 날 시스템 지표와 칸별 성적 (2026-10-16, docs/impl/150 8번). 기간 규칙은 fetchMyTrades 와 같다.
 */
export async function fetchEntryIndicators(days: number): Promise<EntryIndicators> {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return api.get<EntryIndicators>('/trading/account/my-trades/entry-indicators', {
    from: kstDateString(from),
    to: kstDateString(to),
  });
}

/** 미국 주식 양도세 어림 — 해마다 한 줄(처음부터 전부) (2026-10-16, docs/impl/150 9번). 신고액이 아니다 */
export async function fetchUsTaxEstimate(): Promise<UsTaxEstimate> {
  return api.get<UsTaxEstimate>('/trading/account/my-trades/us-tax');
}

// ── 섹터 (2026-08-23) ──

/** 섹터 목록과 각 섹터의 종목 수. */
export async function fetchSectors(): Promise<SectorSummary[]> {
  return api.get<SectorSummary[]>('/trading/sectors');
}

/**
 * 한 섹터의 종목. 시가총액 내림차순이다.
 *
 * 🔴 코드에 한글과 가운뎃점이 들어간다 (반도체·AI). 반드시 인코딩해 보낸다.
 *
 * 🔴 limit 을 보내지 않는다 (2026-09-10 사용자 지적)
 *   여기서 30을 박아 보내고 있었다. 백엔드 상한은 그 사이 50으로 올랐는데도
 *   화면은 계속 30만 받아, 탭에 46 이라고 적힌 섹터를 눌러도 30개만 나왔다.
 *   상한을 지우고 섹터 전체를 받는다 - 탭의 숫자와 목록 길이가 늘 같아진다.
 */
export async function fetchSectorStocks(
  code: string,
  /** 상위 몇 개만 받고 싶을 때만 준다. 안 주면 전체 */
  limit?: number,
): Promise<SectorStock[]> {
  return api.get<SectorStock[]>(
    `/trading/sectors/${encodeURIComponent(code)}/stocks`,
    limit === undefined ? undefined : { limit },
  );
}

// ── 주도주 (V128, docs/impl/113) ──

/** 관리자가 지정한 주도 섹터. */
export async function fetchLeadingSectors(): Promise<LeadingSectorRef[]> {
  return api.get<LeadingSectorRef[]>('/trading/leading/sectors');
}

/**
 * 주도주 탭에 보일 최대 종목 수 — 이 값을 서버에 명시해 보낸다(서버 기본값은 화면 경로에서 안 쓰인다).
 *
 * ✏️ 2026-10-09 50 → 100 (사용자 결정 — 9/10 에는 "100은 폰에서 스크롤이 너무 길다" 로 50 이었다).
 *   주도 섹터 두 곳의 분류 종목이 약 96개라(141번) 100 이면 사실상 전부 보인다.
 *   화면 문구도 이 상수를 그린다 — 9/10 에 기본 인자와 서버 상한 중 한쪽만 고친 일이 있었다(113번).
 *   🔴 시세 API 는 한 번에 200종목까지다(TossPriceClient) — 200 을 넘기지 않는다.
 */
export const LEADING_STOCK_LIMIT = 100;

/**
 * 주도 섹터 종목. 거래대금 상위 limit 개.
 *
 * 화면에 그리는 순서는 여기가 아니라 화면이 정한다 -
 * 자르는 기준(거래대금)과 보는 순서(코스피 먼저·현재가 순)가 다르다.
 */
export async function fetchLeadingStocks(limit = LEADING_STOCK_LIMIT): Promise<LeadingStock[]> {
  return api.get<LeadingStock[]>('/trading/leading/stocks', { limit });
}

// ── 환율 (2026-08-20) ──

/**
 * 최신 USD → KRW 환율. 미국 가격의 원화 병기에 쓴다.
 *
 * 🔴 배포 직후 첫 배치(매시 정각) 전에는 null 이다. 화면은 병기를 숨긴다.
 */
export async function fetchExchangeRate(): Promise<ExchangeRateInfo | null> {
  return api.get<ExchangeRateInfo | null>('/trading/exchange-rate');
}

/**
 * 지금 USD → KRW 환율. 환율 탭 맨 위 숫자. (2026-09-19, I-61)
 *
 * 서버가 60초 캐시하고 토스가 실패하면 저장된 최신값을 준다 - fetchedAt 이 그 시각이다.
 * 둘 다 없으면(배포 직후 첫 정각 전) null 이다.
 */
export async function fetchLiveExchangeRate(): Promise<ExchangeRateInfo | null> {
  return api.get<ExchangeRateInfo | null>('/trading/exchange-rate/live');
}

/** 최근 며칠의 매시 환율(오래된 것부터). 환율 탭 추이선 (2026-09-19, I-61) */
export async function fetchExchangeRateHistory(days: number): Promise<ExchangeRateInfo[]> {
  return api.get<ExchangeRateInfo[]>('/trading/exchange-rate/history', { days });
}

// ── 저점매수 사다리 (2026-10-05, BACKLOG I-89) ──

/** 종목 모달 사다리 칸. 조회만 한다 - 알림은 setPriceAlert 를 단마다 부른다 */
export async function fetchLadderGuide(stockId: number): Promise<LadderGuide> {
  return api.get<LadderGuide>(`/trading/stocks/${stockId}/ladder-guide`);
}

// ── 일자별 시세 (2026-08-28) ──

/**
 * 증권사 앱의 "일자별" 표. before 미지정이면 최신부터 30줄.
 * 응답의 nextBefore 를 그대로 넣으면 이어진다. 서버가 1년에서 끊는다.
 */
export async function fetchDailyPrices(
  stockId: number,
  before?: string,
): Promise<DailyPriceSlice> {
  return api.get<DailyPriceSlice>(
    `/trading/stocks/${stockId}/daily-prices`,
    before === undefined ? undefined : { before },
  );
}
